import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  Code,
  Trash2,
  Eye,
  Shield,
  KeyRound,
  LogIn,
  LogOut,
  Mail,
  Search
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

// ==================== Error Logs Tab ====================
const ErrorLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    module: '',
    severity: '',
    error_type: '',
    resolved: '',
    days: '30'
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);

  useEffect(() => { fetchData(); }, [page, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', '25');
      params.append('days', filters.days);
      if (filters.module) params.append('module', filters.module);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.error_type) params.append('error_type', filters.error_type);
      if (filters.resolved === 'true') params.append('resolved', 'true');
      if (filters.resolved === 'false') params.append('resolved', 'false');

      const [logsRes, summaryRes] = await Promise.all([
        api.get(`/audit/errors/list?${params.toString()}`),
        api.get(`/audit/errors/summary?days=${filters.days}`)
      ]);
      setLogs(logsRes.data.logs);
      setTotalPages(logsRes.data.total_pages);
      setTotal(logsRes.data.total);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (errorId) => {
    try {
      await api.put(`/audit/errors/${errorId}/resolve`);
      toast.success('Error marked as resolved');
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  const handleClearResolved = async () => {
    if (!window.confirm('Delete all resolved errors older than 30 days?')) return;
    try {
      const response = await api.delete('/audit/errors/clear-resolved?days_old=30');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to clear resolved errors');
    }
  };

  const getSeverityBadge = (severity) => {
    const map = {
      critical: <Badge className="bg-red-600 text-white">Critical</Badge>,
      error: <Badge className="bg-red-100 text-red-700">Error</Badge>,
      warning: <Badge className="bg-amber-100 text-amber-700">Warning</Badge>,
    };
    return map[severity] || <Badge className="bg-slate-100 text-slate-700">{severity}</Badge>;
  };

  const getSeverityIcon = (severity) => {
    const map = {
      critical: <XCircle className="h-5 w-5 text-red-600" />,
      error: <AlertCircle className="h-5 w-5 text-red-500" />,
      warning: <AlertTriangle className="h-5 w-5 text-amber-500" />,
    };
    return map[severity] || <AlertCircle className="h-5 w-5 text-slate-400" />;
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '-';

  if (loading && !logs.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleClearResolved}>
          <Trash2 className="h-4 w-4 mr-2" />Clear Resolved
        </Button>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-100 rounded-lg"><AlertCircle className="h-5 w-5 text-slate-600" /></div>
              <div><p className="text-xs text-slate-500">Total</p><p className="text-xl font-bold">{summary.total_errors}</p></div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-lg"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
              <div><p className="text-xs text-slate-500">Unresolved</p><p className="text-xl font-bold text-amber-600">{summary.unresolved}</p></div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-100 rounded-lg"><XCircle className="h-5 w-5 text-red-600" /></div>
              <div><p className="text-xs text-slate-500">Critical</p><p className="text-xl font-bold text-red-600">{summary.critical}</p></div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div><p className="text-xs text-slate-500">Resolved</p><p className="text-xl font-bold text-green-600">{summary.total_errors - summary.unresolved}</p></div>
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <Card><CardContent className="pt-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-40">
            <label className="text-sm text-slate-500 mb-1 block">Module</label>
            <Select value={filters.module || "all"} onValueChange={(v) => { setFilters({...filters, module: v === "all" ? "" : v}); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="All modules" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modules</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
                <SelectItem value="enrollment">Enrollment</SelectItem>
                <SelectItem value="auth">Auth</SelectItem>
                <SelectItem value="database">Database</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <label className="text-sm text-slate-500 mb-1 block">Severity</label>
            <Select value={filters.severity || "all"} onValueChange={(v) => { setFilters({...filters, severity: v === "all" ? "" : v}); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <label className="text-sm text-slate-500 mb-1 block">Status</label>
            <Select value={filters.resolved || "all"} onValueChange={(v) => { setFilters({...filters, resolved: v === "all" ? "" : v}); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="false">Unresolved</SelectItem>
                <SelectItem value="true">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <label className="text-sm text-slate-500 mb-1 block">Time Range</label>
            <Select value={filters.days} onValueChange={(v) => { setFilters({...filters, days: v}); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">24 hours</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => { setFilters({ module: '', severity: '', error_type: '', resolved: '', days: '30' }); setPage(1); }}>
            Clear Filters
          </Button>
        </div>
      </CardContent></Card>

      {/* Error List */}
      <Card>
        <CardHeader><CardTitle>Error Logs ({total})</CardTitle></CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-700">No errors found</p>
              <p className="text-slate-500">Great news! No errors match your filters.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map(log => (
                <div key={log.id} className={`border rounded-lg p-4 ${
                  log.resolved ? 'bg-slate-50 border-slate-200' :
                  log.severity === 'critical' ? 'bg-red-50 border-red-200' :
                  log.severity === 'error' ? 'bg-red-50/50 border-red-100' :
                  'bg-amber-50/50 border-amber-100'
                }`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {getSeverityIcon(log.severity)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{log.error_type}</span>
                          {getSeverityBadge(log.severity)}
                          <Badge variant="outline">{log.module}</Badge>
                          {log.resolved && <Badge className="bg-green-100 text-green-700">Resolved</Badge>}
                        </div>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{log.error_message}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(log.timestamp)}</span>
                          {log.endpoint && <span className="flex items-center gap-1"><Code className="h-3 w-3" />{log.endpoint}</span>}
                          {log.user_email && <span className="flex items-center gap-1"><User className="h-3 w-3" />{log.user_email}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedLog(log); setDetailDialog(true); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {!log.resolved && (
                        <Button variant="outline" size="sm" onClick={() => handleResolve(log.id)} className="text-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" />Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-slate-500">Page {page} of {totalPages} ({total} total)</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-4 w-4" />Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Next<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLog && getSeverityIcon(selectedLog.severity)} Error Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-500">Error Type</p><p className="font-medium">{selectedLog.error_type}</p></div>
                <div><p className="text-sm text-slate-500">Severity</p>{getSeverityBadge(selectedLog.severity)}</div>
                <div><p className="text-sm text-slate-500">Module</p><p className="font-medium">{selectedLog.module}</p></div>
                <div><p className="text-sm text-slate-500">Endpoint</p><p className="font-medium font-mono text-sm">{selectedLog.endpoint || '-'}</p></div>
                <div><p className="text-sm text-slate-500">Timestamp</p><p className="font-medium">{formatDate(selectedLog.timestamp)}</p></div>
                <div><p className="text-sm text-slate-500">Status</p>
                  {selectedLog.resolved
                    ? <Badge className="bg-green-100 text-green-700">Resolved</Badge>
                    : <Badge className="bg-amber-100 text-amber-700">Unresolved</Badge>}
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Error Message</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3"><p className="text-sm text-red-800">{selectedLog.error_message}</p></div>
              </div>
              {selectedLog.user_email && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">User Context</p>
                  <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    <p><strong>Email:</strong> {selectedLog.user_email}</p>
                    {selectedLog.user_role && <p><strong>Role:</strong> {selectedLog.user_role}</p>}
                  </div>
                </div>
              )}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Additional Context</p>
                  <pre className="bg-slate-100 rounded-lg p-3 text-xs overflow-x-auto">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                </div>
              )}
              {selectedLog.stack_trace && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Stack Trace</p>
                  <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto max-h-48">{selectedLog.stack_trace}</pre>
                </div>
              )}
              {selectedLog.resolved && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800"><strong>Resolved by:</strong> {selectedLog.resolved_by_name || 'Unknown'}</p>
                  <p className="text-sm text-green-700"><strong>Resolved at:</strong> {formatDate(selectedLog.resolved_at)}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog(false)}>Close</Button>
            {selectedLog && !selectedLog.resolved && (
              <Button onClick={() => { handleResolve(selectedLog.id); setDetailDialog(false); }} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />Mark as Resolved
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== System / Auth Logs Tab ====================
const SystemLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [limit, setLimit] = useState('100');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);

  useEffect(() => { fetchLogs(); }, [typeFilter, limit]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      if (typeFilter) params.append('log_type', typeFilter);
      if (emailFilter.trim()) params.append('email', emailFilter.trim());

      const res = await api.get(`/admin/system-logs?${params.toString()}`);
      setLogs(res.data.logs);
      setStats(res.data.stats);
    } catch (error) {
      toast.error('Failed to fetch system logs');
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Delete system logs older than 30 days?')) return;
    try {
      const res = await api.delete('/admin/system-logs/clear?older_than_days=30');
      toast.success(res.data.message);
      fetchLogs();
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  const handleEmailSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const getTypeIcon = (type) => {
    const map = {
      auth_failure: <XCircle className="h-4 w-4 text-red-500" />,
      auth_success: <LogIn className="h-4 w-4 text-green-500" />,
      password_reset: <KeyRound className="h-4 w-4 text-blue-500" />,
      password_reset_request: <Mail className="h-4 w-4 text-amber-500" />,
      password_reset_email: <Mail className="h-4 w-4 text-purple-500" />,
      logout: <LogOut className="h-4 w-4 text-slate-400" />,
    };
    return map[type] || <Shield className="h-4 w-4 text-slate-400" />;
  };

  const getTypeBadge = (type) => {
    const map = {
      auth_failure: <Badge className="bg-red-100 text-red-700">Login Failed</Badge>,
      auth_success: <Badge className="bg-green-100 text-green-700">Login Success</Badge>,
      password_reset: <Badge className="bg-blue-100 text-blue-700">Password Reset</Badge>,
      password_reset_request: <Badge className="bg-amber-100 text-amber-700">Reset Request</Badge>,
      password_reset_email: <Badge className="bg-purple-100 text-purple-700">Reset Email</Badge>,
      logout: <Badge className="bg-slate-100 text-slate-700">Logout</Badge>,
    };
    return map[type] || <Badge variant="outline">{type}</Badge>;
  };

  const getRowBg = (type) => {
    if (type === 'auth_failure') return 'bg-red-50/60 border-red-100';
    if (type === 'password_reset_request') return 'bg-amber-50/60 border-amber-100';
    if (type === 'password_reset_email') return 'bg-purple-50/40 border-purple-100';
    if (type === 'password_reset') return 'bg-blue-50/40 border-blue-100';
    return 'bg-white border-slate-200';
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '-';

  if (loading && !logs.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-100 rounded-lg"><XCircle className="h-5 w-5 text-red-600" /></div>
              <div><p className="text-xs text-slate-500">Failed Logins</p><p className="text-xl font-bold text-red-600">{stats.total_auth_failures}</p></div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-lg"><Mail className="h-5 w-5 text-amber-600" /></div>
              <div><p className="text-xs text-slate-500">Reset Requests</p><p className="text-xl font-bold text-amber-600">{stats.total_reset_requests}</p></div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-lg"><KeyRound className="h-5 w-5 text-blue-600" /></div>
              <div><p className="text-xs text-slate-500">Password Resets</p><p className="text-xl font-bold text-blue-600">{stats.total_password_resets}</p></div>
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <Card><CardContent className="pt-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-48">
            <label className="text-sm text-slate-500 mb-1 block">Event Type</label>
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="All events" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All events</SelectItem>
                <SelectItem value="auth_failure">Login Failures</SelectItem>
                <SelectItem value="auth_success">Login Successes</SelectItem>
                <SelectItem value="password_reset_request">Reset Requests</SelectItem>
                <SelectItem value="password_reset_email">Reset Emails Sent</SelectItem>
                <SelectItem value="password_reset">Password Resets</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <label className="text-sm text-slate-500 mb-1 block">Show</label>
            <Select value={limit} onValueChange={setLimit}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="200">200</SelectItem>
                <SelectItem value="500">500</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <form onSubmit={handleEmailSearch} className="flex gap-2 items-end">
            <div className="w-56">
              <label className="text-sm text-slate-500 mb-1 block">Search by Email</label>
              <Input
                placeholder="user@example.com"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                data-testid="system-log-email-search"
              />
            </div>
            <Button type="submit" variant="outline" size="icon" data-testid="system-log-search-btn">
              <Search className="h-4 w-4" />
            </Button>
          </form>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={handleClearLogs}>
              <Trash2 className="h-4 w-4 mr-2" />Clear Old
            </Button>
            <Button variant="outline" size="sm" onClick={fetchLogs}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
          </div>
        </div>
      </CardContent></Card>

      {/* Logs List */}
      <Card>
        <CardHeader><CardTitle>System Logs ({logs.length})</CardTitle></CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-700">No system logs found</p>
              <p className="text-slate-500">No events match your current filters.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg p-3 cursor-pointer hover:shadow-sm transition-shadow ${getRowBg(log.type)}`}
                  onClick={() => { setSelectedLog(log); setDetailDialog(true); }}
                  data-testid={`system-log-row-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    {getTypeIcon(log.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getTypeBadge(log.type)}
                        <span className="text-sm font-medium text-slate-800">{log.email || 'Unknown'}</span>
                        {log.action && <span className="text-xs text-slate-500">({log.action})</span>}
                      </div>
                      {log.reason && <p className="text-xs text-slate-500 mt-0.5">{log.reason}</p>}
                      {log.details && <p className="text-xs text-slate-500 mt-0.5">{log.details}</p>}
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(log.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLog && getTypeIcon(selectedLog.type)} Event Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Event Type</p>
                  {getTypeBadge(selectedLog.type)}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Timestamp</p>
                  <p className="font-medium text-sm">{formatDate(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedLog.email || '-'}</p>
                </div>
                {selectedLog.action && (
                  <div>
                    <p className="text-sm text-slate-500">Action</p>
                    <p className="font-medium">{selectedLog.action}</p>
                  </div>
                )}
              </div>

              {selectedLog.reason && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Reason</p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{selectedLog.reason}</p>
                  </div>
                </div>
              )}

              {selectedLog.details && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Details</p>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-sm text-slate-700">{selectedLog.details}</p>
                  </div>
                </div>
              )}

              {selectedLog.ip_address && (
                <div>
                  <p className="text-sm text-slate-500">IP Address</p>
                  <p className="font-mono text-sm">{selectedLog.ip_address}</p>
                </div>
              )}

              {selectedLog.user_agent && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">User Agent</p>
                  <p className="text-xs text-slate-600 font-mono bg-slate-50 p-2 rounded break-all">{selectedLog.user_agent}</p>
                </div>
              )}

              {/* Show all other fields as JSON for debugging */}
              {(() => {
                const knownKeys = ['type', 'action', 'email', 'timestamp', 'reason', 'details', 'ip_address', 'user_agent'];
                const extra = Object.entries(selectedLog).filter(([k]) => !knownKeys.includes(k) && selectedLog[k] != null);
                if (extra.length === 0) return null;
                return (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Additional Data</p>
                    <pre className="bg-slate-100 rounded-lg p-3 text-xs overflow-x-auto">
                      {JSON.stringify(Object.fromEntries(extra), null, 2)}
                    </pre>
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Main ErrorLogs Page with Tabs ====================
const ErrorLogs = () => {
  return (
    <DashboardLayout title="Error Logs">
      <div className="space-y-6" data-testid="error-logs-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Logs & Monitoring</h1>
          <p className="text-slate-500">Application errors and auth/system event tracking</p>
        </div>

        <Tabs defaultValue="errors">
          <TabsList>
            <TabsTrigger value="errors" data-testid="error-logs-tab">
              <AlertCircle className="h-4 w-4 mr-2" />
              Error Logs
            </TabsTrigger>
            <TabsTrigger value="system" data-testid="system-logs-tab">
              <Shield className="h-4 w-4 mr-2" />
              Auth & System Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="errors" className="mt-6">
            <ErrorLogsTab />
          </TabsContent>
          <TabsContent value="system" className="mt-6">
            <SystemLogsTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default ErrorLogs;
